import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { DetailSkeleton } from '@/components/ui/skeleton-presets'
import { EmptyState } from '@/components/EmptyState'
import { CircleAlert, PlaneTakeoff, X } from '@/components/icons'
import { FlightLogDrawer } from '@/components/FlightLogDrawer'
import { DraftLogPill } from '@/components/flight-log/FlightLogNumberBadge'
import { useFlightLogGetMyPendingLogs, type PendingFlightLog } from '@/lib/hooks'
import { hapticLight } from '@/lib/haptics'

interface FlightLogsAttentionDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Pre-built rows to show instead of the pilot's global pending queue.
   * TripDetailView passes the current trip's incomplete legs so the drawer
   * matches the ring card that opened it. When set, no fetch happens.
   */
  logs?: PendingFlightLog[]
}

/**
 * Pilot attention queue (Figma 12495:68938): every draft flight log assigned
 * to the current pilot — or, when `logs` is provided, a caller-scoped list
 * (e.g. one trip's incomplete legs). Tapping a row opens the editable
 * technical log, resumed at its first incomplete section (or the Defects
 * section when a deferred defect is flagged).
 */
export function FlightLogsAttentionDrawer({ open, onOpenChange, logs: logsProp }: FlightLogsAttentionDrawerProps) {
  const navigate = useNavigate()
  const { data, isLoading } = useFlightLogGetMyPendingLogs(open && !logsProp)
  const logs = logsProp ?? ((data as PendingFlightLog[] | undefined) ?? [])
  // Log sticks around after close so the drawer can animate out
  const [selectedLog, setSelectedLog] = useState<PendingFlightLog | null>(null)
  const [isLogOpen, setIsLogOpen] = useState(false)

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="bg-surface-L0 max-h-[85dvh] flex flex-col">
          <DrawerHeader className="flex items-center justify-between p-4">
            <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
              Flight Logs
            </DrawerTitle>
            <button
              onClick={() => onOpenChange(false)}
              className="size-8 rounded-full bg-surface-L2 flex items-center justify-center hover:bg-surface-L2/80 transition-colors shrink-0"
              aria-label="Close"
            >
              <X className="size-4 text-content-primary-subtle" />
            </button>
          </DrawerHeader>

          <div className="px-4 pb-4">
            {!logsProp && isLoading ? (
              <DetailSkeleton rows={4} />
            ) : !logs.length ? (
              <EmptyState
                icon={PlaneTakeoff}
                title="All caught up"
                description="Flight logs that need completing will appear here."
              />
            ) : (
              <div className="flex flex-col gap-2">
                {logs.map((log) => (
                  <button
                    key={log.id}
                    type="button"
                    onClick={() => {
                      hapticLight()
                      setSelectedLog(log)
                      setIsLogOpen(true)
                    }}
                    className="flex items-center justify-between gap-2 rounded-2xl border border-border-L1 bg-surface-L0 px-4 py-2 text-left shadow-[0px_1px_3.5px_rgba(0,0,0,0.05)]"
                  >
                    <p className="min-w-0 truncate text-sm leading-5">
                      <span className="font-bold text-content-primary-bold">
                        {log.departureAirportIcao} → {log.arrivalAirportIcao}
                      </span>
                      {log.aircraftRegistration && (
                        <span className="text-content-primary-subtle">
                          {'  •  '}
                          {log.aircraftRegistration}
                        </span>
                      )}
                      <span className="text-content-primary-subtle">
                        {'  •  '}
                        {dayjs(log.departureDateTime).format('D MMM')}
                      </span>
                    </p>
                    <span className="flex shrink-0 items-center gap-2">
                      {/* Every row here is incomplete, so a numbered log is a draft;
                          a never-started leg has no number yet. */}
                      {log.logNumber !== null && <DraftLogPill logNumber={log.logNumber} />}
                      <span className="flex h-[25px] w-[25px] shrink-0 items-center justify-center rounded-full bg-background-notice-subtle">
                        <CircleAlert className="size-4 text-background-notice-bold" />
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      {selectedLog && (
        <FlightLogDrawer
          isOpen={isLogOpen}
          onClose={() => setIsLogOpen(false)}
          tripLegId={selectedLog.tripLegId}
          initialSection={selectedLog.defectDeferred ? 'defects' : undefined}
          onViewTrip={() => {
            const tripId = selectedLog.tripId
            navigate({
              to: '/schedule/$tripId',
              params: { tripId },
              search: { legId: selectedLog.tripLegId },
            } as any)
          }}
        />
      )}
    </>
  )
}
